import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, Trash2, Upload, Download, TrendingUp, UserPlus, Building2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { TOP_CURRENCIES } from '@/constants/currencies';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/hiring`;

const EnhancedEmployeeManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({});
  const [currencyRates, setCurrencyRates] = useState({});
  const { toast } = useToast();

  useEffect(() => {
    fetchEmployees();
    fetchCurrencyRates();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast({ title: 'Error', description: 'Failed to fetch employees', variant: 'destructive' });
      setLoading(false);
    }
  };

  const fetchCurrencyRates = async () => {
    try {
      const response = await axios.get(`${API}/currency-rates`);
      const ratesMap = {};
      response.data.forEach(rate => {
        ratesMap[rate.currency] = rate.rate_to_usd;
      });
      setCurrencyRates(ratesMap);
    } catch (error) {
      console.error('Error fetching currency rates:', error);
    }
  };

  const handleOpenDialog = (employee = null) => {
    if (employee) {
      setEditingEmployee(employee);
      setFormData(employee);
    } else {
      setEditingEmployee(null);
      setFormData({
        employee_id: `EMP${Date.now().toString().slice(-6)}`,
        name: '',
        start_date: '',
        end_date: '',
        employee_type: 'Full-time',
        job_title: '',
        team: '',
        function: '',
        location_type: 'Onshore',
        manager_name: '',
        cost_center: '',
        salary_local: 0,
        currency: 'USD',
        country: '',
        salary_usd: 0,
        benefits_percentage: 0.15,
        taxes_percentage: 0.2,
        bonus_percentage: 0,
        gender: 'Male',
        date_of_birth: '',
        civil_status: 'Single',
        employment_status: 'Permanent'
      });
    }
    setIsDialogOpen(true);
  };

  const handleCurrencyChange = (currency) => {
    const newFormData = { ...formData, currency };
    if (currency !== 'USD' && currencyRates[currency] && formData.salary_local) {
      newFormData.salary_usd = formData.salary_local * currencyRates[currency];
    } else if (currency === 'USD') {
      newFormData.salary_usd = formData.salary_local;
    }
    setFormData(newFormData);
  };

  const handleSalaryLocalChange = (value) => {
    const salary_local = parseFloat(value) || 0;
    const newFormData = { ...formData, salary_local };
    if (formData.currency !== 'USD' && currencyRates[formData.currency]) {
      newFormData.salary_usd = salary_local * currencyRates[formData.currency];
    } else {
      newFormData.salary_usd = salary_local;
    }
    setFormData(newFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEmployee) {
        await axios.put(`${API}/employees/${editingEmployee.id}`, formData);
        toast({ title: 'Success', description: 'Employee updated successfully' });
      } else {
        await axios.post(`${API}/employees`, formData);
        toast({ title: 'Success', description: 'Employee created successfully' });
      }
      setIsDialogOpen(false);
      fetchEmployees();
    } catch (error) {
      console.error('Error saving employee:', error);
      toast({ title: 'Error', description: 'Failed to save employee', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) return;
    
    try {
      await axios.delete(`${API}/employees/${id}`);
      toast({ title: 'Success', description: 'Employee deleted successfully' });
      fetchEmployees();
    } catch (error) {
      console.error('Error deleting employee:', error);
      toast({ title: 'Error', description: 'Failed to delete employee', variant: 'destructive' });
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const importFormData = new FormData();
    importFormData.append('file', file);

    try {
      const response = await axios.post(`${API}/import-employees`, importFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const result = response.data;
      
      if (result.failed_count > 0) {
        // Show detailed errors
        const errorList = result.errors.slice(0, 10).join('\n');
        toast({ 
          title: 'Import Completed with Errors', 
          description: `Imported: ${result.imported_count}, Failed: ${result.failed_count}\n\nFirst errors:\n${errorList}`,
          variant: 'destructive',
          duration: 10000
        });
      } else {
        toast({ 
          title: 'Import Successful', 
          description: `Successfully imported ${result.imported_count} employees` 
        });
      }
      
      fetchEmployees();
    } catch (error) {
      console.error('Error importing employees:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to import employees';
      toast({ 
        title: 'Import Failed', 
        description: errorMsg, 
        variant: 'destructive',
        duration: 10000
      });
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/download-template`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'employee_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({ title: 'Success', description: 'Template downloaded successfully' });
    } catch (error) {
      console.error('Error downloading template:', error);
      toast({ title: 'Error', description: 'Failed to download template', variant: 'destructive' });
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/export-employees`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'employees_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({ title: 'Success', description: 'Employees exported successfully' });
    } catch (error) {
      console.error('Error exporting employees:', error);
      toast({ title: 'Error', description: 'Failed to export employees', variant: 'destructive' });
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  const activeEmployees = employees.filter(e => !e.end_date || new Date(e.end_date) >= new Date());
  const inactiveEmployees = employees.filter(e => e.end_date && new Date(e.end_date) < new Date());

  return (
    <div className="p-6 space-y-6 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Employee Management</h1>
          <p className="text-gray-600 mt-1">Manage workforce and organizational structure</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={handleDownloadTemplate} variant="outline" className="shadow-sm">
            <Download className="mr-2 h-4 w-4" />
            Download Template
          </Button>
          <Button onClick={handleExport} variant="outline" className="shadow-sm">
            <Download className="mr-2 h-4 w-4" />
            Export Data
          </Button>
          <label htmlFor="file-upload">
            <Button variant="outline" asChild className="shadow-sm">
              <span>
                <Upload className="mr-2 h-4 w-4" />
                Import CSV
              </span>
            </Button>
          </label>
          <input
            id="file-upload"
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleImport}
            className="hidden"
          />
          <Button onClick={() => handleOpenDialog()} className="shadow-md bg-gradient-to-r from-[#C9A64A] to-[#D4B76A] hover:from-[#C9A64A] hover:to-[#D4B76A]">
            <Plus className="mr-2 h-4 w-4" />
            Add Employee
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="shadow-md border-l-4 border-l-blue-500">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Total Employees</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{employees.length}</div>
          </CardContent>
        </Card>
        <Card className="shadow-md border-l-4 border-l-green-500">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Active</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">{activeEmployees.length}</div>
          </CardContent>
        </Card>
        <Card className="shadow-md border-l-4 border-l-orange-500">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Inactive</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600">{inactiveEmployees.length}</div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="active" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="active">Active Employees</TabsTrigger>
          <TabsTrigger value="inactive">Inactive Employees</TabsTrigger>
        </TabsList>

        <TabsContent value="active">
          <Card className="shadow-md">
            <CardContent className="pt-6">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Employee ID</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Job Title</TableHead>
                      <TableHead>Team</TableHead>
                      <TableHead>Manager</TableHead>
                      <TableHead>Cost Center</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Salary (USD)</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activeEmployees.map((employee) => (
                      <TableRow key={employee.id} className="hover:bg-slate-50">
                        <TableCell className="font-mono text-sm">{employee.employee_id || '-'}</TableCell>
                        <TableCell className="font-medium">{employee.name}</TableCell>
                        <TableCell>{employee.job_title}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{employee.team}</Badge>
                        </TableCell>
                        <TableCell>{employee.manager_name || '-'}</TableCell>
                        <TableCell>{employee.cost_center || '-'}</TableCell>
                        <TableCell>
                          <Badge variant={employee.employee_type === 'Full-time' ? 'default' : 'secondary'}>
                            {employee.employee_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-semibold">${employee.salary_usd?.toLocaleString()}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenDialog(employee)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(employee.id)}
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="inactive">
          <Card className="shadow-md">
            <CardContent className="pt-6">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Employee ID</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Job Title</TableHead>
                      <TableHead>End Date</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inactiveEmployees.map((employee) => (
                      <TableRow key={employee.id} className="opacity-60">
                        <TableCell className="font-mono text-sm">{employee.employee_id || '-'}</TableCell>
                        <TableCell>{employee.name}</TableCell>
                        <TableCell>{employee.job_title}</TableCell>
                        <TableCell>{new Date(employee.end_date).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(employee.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Employee Dialog - continued in next part */}

      {/* Employee Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {editingEmployee ? 'Edit Employee' : 'Add New Employee'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="employee_id">Employee ID *</Label>
                <Input
                  id="employee_id"
                  value={formData.employee_id || ''}
                  onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="job_title">Job Title *</Label>
                <Input
                  id="job_title"
                  value={formData.job_title || ''}
                  onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="employee_type">Employment Type *</Label>
                <Select
                  value={formData.employee_type}
                  onValueChange={(value) => setFormData({ ...formData, employee_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Full-time">Full-time</SelectItem>
                    <SelectItem value="Contractor">Contractor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="team">Team *</Label>
                <Input
                  id="team"
                  value={formData.team || ''}
                  onChange={(e) => setFormData({ ...formData, team: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="function">Function *</Label>
                <Input
                  id="function"
                  value={formData.function || ''}
                  onChange={(e) => setFormData({ ...formData, function: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="manager_name">Manager Name</Label>
                <Input
                  id="manager_name"
                  value={formData.manager_name || ''}
                  onChange={(e) => setFormData({ ...formData, manager_name: e.target.value })}
                  placeholder="Leave empty if no manager"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cost_center">Cost Center</Label>
                <Input
                  id="cost_center"
                  value={formData.cost_center || ''}
                  onChange={(e) => setFormData({ ...formData, cost_center: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="location_type">Location *</Label>
                <Select
                  value={formData.location_type}
                  onValueChange={(value) => setFormData({ ...formData, location_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Onshore">Onshore</SelectItem>
                    <SelectItem value="Offshore">Offshore</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="country">Country *</Label>
                <Input
                  id="country"
                  value={formData.country || ''}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="currency">Currency *</Label>
                <Select
                  value={formData.currency}
                  onValueChange={handleCurrencyChange}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TOP_CURRENCIES.map((curr) => (
                      <SelectItem key={curr.code} value={curr.code}>
                        {curr.symbol} {curr.name} ({curr.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="salary_local">Salary (Local Currency) *</Label>
                <Input
                  id="salary_local"
                  type="number"
                  value={formData.salary_local || 0}
                  onChange={(e) => handleSalaryLocalChange(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="salary_usd">Salary (USD) - Auto-calculated</Label>
                <Input
                  id="salary_usd"
                  type="number"
                  value={Math.round(formData.salary_usd || 0)}
                  readOnly
                  className="bg-slate-50"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="benefits_percentage">Benefits % *</Label>
                <Input
                  id="benefits_percentage"
                  type="number"
                  step="0.01"
                  value={formData.benefits_percentage || 0}
                  onChange={(e) => setFormData({ ...formData, benefits_percentage: parseFloat(e.target.value) })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="taxes_percentage">Taxes % *</Label>
                <Input
                  id="taxes_percentage"
                  type="number"
                  step="0.01"
                  value={formData.taxes_percentage || 0}
                  onChange={(e) => setFormData({ ...formData, taxes_percentage: parseFloat(e.target.value) })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="start_date">Start Date *</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date || ''}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date">End Date (if applicable)</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={formData.end_date || ''}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
              </div>
            </div>

            {/* KPI Fields Section */}
            <div className="pt-4 border-t">
              <h3 className="font-semibold mb-3">KPI & Demographics</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="gender">Gender *</Label>
                  <Select
                    value={formData.gender}
                    onValueChange={(value) => setFormData({ ...formData, gender: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date_of_birth">Date of Birth *</Label>
                  <Input
                    id="date_of_birth"
                    type="date"
                    value={formData.date_of_birth || ''}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="civil_status">Civil Status *</Label>
                  <Select
                    value={formData.civil_status}
                    onValueChange={(value) => setFormData({ ...formData, civil_status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Single">Single</SelectItem>
                      <SelectItem value="Married">Married</SelectItem>
                      <SelectItem value="Divorced">Divorced</SelectItem>
                      <SelectItem value="Widowed">Widowed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="employment_status">Employment Status *</Label>
                  <Select
                    value={formData.employment_status}
                    onValueChange={(value) => setFormData({ ...formData, employment_status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Permanent">Permanent</SelectItem>
                      <SelectItem value="Temporary">Temporary</SelectItem>
                      <SelectItem value="Maternity">Maternity</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-gradient-to-r from-[#C9A64A] to-[#D4B76A]">
                {editingEmployee ? 'Update' : 'Create'} Employee
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EnhancedEmployeeManagement;
